import { defineComponent as _defineComponent } from 'vue'
import { ref, onBeforeUnmount } from 'vue';
import clerk, { getMessageKeyByClerkError } from '@/lib/clerk';
import { dispatchLogOut } from '@/store/main/actionsMain';
import { store } from '@/store/index';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import LogoutConfirmDialog from './LogoutConfirmDialog.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SmsAuth',
  setup(__props) {

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  countryCode: '国コード',
  selectCountry: '国を選択してください',
  invalidPhoneNumber:
    '無効な電話番号です。10から15桁の数字を入力してください。',
  disclaimer: '税金やデータ料金が適用される場合があります',
  sendCode: 'コードを送信',
  codeSentInfo:
    'お客様のアカウントに関連付けられている電話番号に送信した認証コードを入力してください。(認証コードは15分間有効です)',
  changePhoneNumber: '電話番号を変更',
  codeResentSuccess: 'コードが再送信されました',
  resendCooldownMessage: '再送信は30秒後に可能です',
  seconds: '秒',
});

const getFlagEmoji = (countryCode) => {
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
};

const selectedCountry = ref({
  name: 'Japan',
  code: 'JP',
  dial_code: '+81',
});
const phoneNumber = ref('');
const fullPhoneNumber = ref('');
const otp = ref('');
const phoneNumberError = ref(false);
const sendingCode = ref(false);
const verificationPending = ref(false);
const codeSent = ref(false);
const waitForCooldown = ref(false);
const codeResent = ref(false);
const cooldownInterval = ref<number | null>(null);
const cooldownTimeLeft = ref(RESEND_DELAY / 1000);
const resendingCode = ref(false);
const isOpenDialog = ref(false);
const errorAlert = ref('');
const otpInput = ref(null);
const phoneObj = ref(null);

let resendCooldown = false; // Variable to manage cooldown state
const RESEND_DELAY = 30000; // 30 seconds cooldown (adjust as needed)

// List of countries with their respective codes
const countryCodes = ref([
  { name: 'Japan', code: 'JP', dial_code: '+81' },
  { name: 'United States', code: 'US', dial_code: '+1' },
  { name: 'United Kingdom', code: 'GB', dial_code: '+44' },
  { name: 'India', code: 'IN', dial_code: '+91' },
  { name: 'Australia', code: 'AU', dial_code: '+61' },
  { name: 'Canada', code: 'CA', dial_code: '+1' },
  { name: 'China', code: 'CN', dial_code: '+86' },
  { name: 'Germany', code: 'DE', dial_code: '+49' },
  { name: 'France', code: 'FR', dial_code: '+33' },
  { name: 'Brazil', code: 'BR', dial_code: '+55' },
  { name: 'South Africa', code: 'ZA', dial_code: '+27' },
  { name: 'Russia', code: 'RU', dial_code: '+7' },
  { name: 'Mexico', code: 'MX', dial_code: '+52' },
  { name: 'Italy', code: 'IT', dial_code: '+39' },
  { name: 'Spain', code: 'ES', dial_code: '+34' },
  { name: 'Sweden', code: 'SE', dial_code: '+46' },
  { name: 'Netherlands', code: 'NL', dial_code: '+31' },
  { name: 'New Zealand', code: 'NZ', dial_code: '+64' },
  { name: 'Switzerland', code: 'CH', dial_code: '+41' },
  { name: 'Turkey', code: 'TR', dial_code: '+90' },
  { name: 'Singapore', code: 'SG', dial_code: '+65' },
  { name: 'Saudi Arabia', code: 'SA', dial_code: '+966' },
  { name: 'Argentina', code: 'AR', dial_code: '+54' },
  { name: 'Malaysia', code: 'MY', dial_code: '+60' },
  { name: 'Thailand', code: 'TH', dial_code: '+66' },
]);

const startPhoneVerification = async (phoneNumber) => {
  errorAlert.value = '';
  sendingCode.value = true;

  try {
    const res = await clerk.user.createPhoneNumber({ phoneNumber });
    phoneObj.value = clerk.user.phoneNumbers.find((a) => a.id === res.id);
    phoneObj.value.prepareVerification();
    codeSent.value = true;
    sendingCode.value = false;
    setTimeout(() => {
      otpInput?.value?.focus();
    });
  } catch (error) {
    console.log(error);
    errorAlert.value = getMessageKeyByClerkError(error);
  } finally {
    sendingCode.value = false;
  }
};

// Function to validate the phone number
const validatePhoneNumber = () => {
  const phoneRegex = /^[0-9]{9,15}$/;
  if (!phoneRegex.test(phoneNumber.value)) {
    phoneNumberError.value = true;
    return false;
  }
  phoneNumberError.value = false;
  return true;
};

// Function to handle sending the code with validation
const handlePhoneNumberSubmit = () => {
  if (validatePhoneNumber()) {
    otp.value = '';
    fullPhoneNumber.value = `${selectedCountry.value.dial_code}${phoneNumber.value}`;
    startPhoneVerification(fullPhoneNumber.value);
  }
};

const verifyCode = async () => {
  errorAlert.value = '';
  verificationPending.value = true;

  try {
    const phoneVerifyAttempt = await phoneObj.value.attemptVerification({
      code: otp.value,
    });

    if (phoneVerifyAttempt.verification.status === 'verified') {
      // Set the phone number as reserved for second factor
      phoneObj.value.setReservedForSecondFactor({ reserved: true });
      // Open the dialog to confirm the logout
      isOpenDialog.value = true;
    } else {
      throw new Error('Could not verify phone number');
    }
  } catch (error) {
    errorAlert.value = getMessageKeyByClerkError(error);
  } finally {
    verificationPending.value = false;
  }
};

const handleLogoutConfirmation = (): void => {
  dispatchLogOut(store, { tfaCompleted: true });
};

const resendCode = async () => {
  if (resendCooldown) {
    alert($t(m.resendCooldownMessage));
    return;
  }

  errorAlert.value = '';
  resendingCode.value = true;
  otp.value = '';
  try {
    await phoneObj.value.prepareVerification();
    codeResent.value = true;
    startResendCooldown();
  } catch (error) {
    errorAlert.value = getMessageKeyByClerkError(error);
  } finally {
    resendingCode.value = false;
  }
};

const submitIfComplete = () => {
  if (!otp.value.match(/^[0-9]*$/)) {
    otp.value = otp.value.replace(/[^0-9]/g, '');
  }
  if (otp.value.length === 6) {
    verifyCode();
  }
};

const handleKeydownOtp = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'Delete'];
  const isDigit = /^\d$/.test(event.key);
  const isProcessKey = event.key === 'Process' || event.key === 'Unidentified';

  if ((!isDigit && !allowedKeys.includes(event.key)) || isProcessKey) {
    event.preventDefault();
  }
};

const handleKeypressPhone = (event: KeyboardEvent) => {
  const charCode = event.charCode ? event.charCode : event.keyCode;
  if (charCode < 48 || charCode > 57) {
    event.preventDefault();
  }
  if (charCode === 13) {
    handlePhoneNumberSubmit();
  }
};

// Start the cooldown timer
const startResendCooldown = () => {
  waitForCooldown.value = true;

  cooldownTimeLeft.value = RESEND_DELAY / 1000;
  cooldownInterval.value = setInterval(() => {
    cooldownTimeLeft.value -= 1;
    if (cooldownTimeLeft.value <= 0) {
      waitForCooldown.value = false;
      clearInterval(cooldownInterval.value);
      cooldownInterval.value = null;
    }
  }, 1000);
};

onBeforeUnmount(() => {
  if (cooldownInterval.value !== null) {
    clearInterval(cooldownInterval.value);
  }
});

return { __sfc: true,$t, m, getFlagEmoji, selectedCountry, phoneNumber, fullPhoneNumber, otp, phoneNumberError, sendingCode, verificationPending, codeSent, waitForCooldown, codeResent, cooldownInterval, cooldownTimeLeft, resendingCode, isOpenDialog, errorAlert, otpInput, phoneObj, resendCooldown, RESEND_DELAY, countryCodes, startPhoneVerification, validatePhoneNumber, handlePhoneNumberSubmit, verifyCode, handleLogoutConfirmation, resendCode, submitIfComplete, handleKeydownOtp, handleKeypressPhone, startResendCooldown, sharedMessages, LogoutConfirmDialog }
}

})